import { APP_ENVIRONMENT } from "@solomon-apps/config";
import { useOrgAuthStore } from "@solomon-apps/shared";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import type { ClientApiKeyMetadata } from "@/services/clientApiKeys";
import {
    createClientApiKey,
    type CreateClientApiKeyInput,
    type CreateClientApiKeyResult,
    listClientApiKeys,
    revokeClientApiKey,
    rotateClientApiKey,
    type RotateClientApiKeyInput,
    type RotateClientApiKeyResult
} from "@/services/clientApiKeys";
import { isAmbiguousIssuanceError, isCurrentClientApiContext } from "@/utils/clientApiKeyWorkflow";

export type RotateClientApiKeyOutcome =
    | { status: "success"; result: RotateClientApiKeyResult }
    | { status: "ambiguous" }
    | { status: "error"; error: unknown }
    | { status: "stale" };

export function clientApiKeysQueryKey(issuerClientUuid: string | null): readonly string[] {
    return ["yaas-client", "client-api-keys", issuerClientUuid ?? "none", APP_ENVIRONMENT];
}
interface UseClientApiKeysResult {
    issuerClientUuid: string | null;
    query: UseQueryResult<ClientApiKeyMetadata[], Error>;
    createKey: (contextUuid: string, input: CreateClientApiKeyInput) => Promise<CreateClientApiKeyResult>;
    isCreating: boolean;
    rotateKey: (id: string, input: RotateClientApiKeyInput) => Promise<RotateClientApiKeyOutcome>;
    rotatingKeyIds: Set<string>;
    revokeMutation: UseMutationResult<void, Error, { contextUuid: string; id: string }, unknown>;
}

export function useClientApiKeys(enabled: boolean): UseClientApiKeysResult {
    const queryClient = useQueryClient();
    const issuerClientUuid = useOrgAuthStore((state) => state.selectedClientContextUuid);
    const queryKey = clientApiKeysQueryKey(issuerClientUuid);
    const query = useQuery({
        queryKey,
        queryFn: () => listClientApiKeys(issuerClientUuid!),
        enabled: enabled && Boolean(issuerClientUuid),
        retry: false
    });
    const createOperations = useRef(new Map<string, Promise<CreateClientApiKeyResult>>());
    const [creatingContexts, setCreatingContexts] = useState<ReadonlySet<string>>(() => new Set());
    const rotateOperations = useRef(new Map<string, Promise<RotateClientApiKeyOutcome>>());
    const [rotatingKeys, setRotatingKeys] = useState<ReadonlySet<string>>(() => new Set());
    const createKey = useCallback(
        async (contextUuid: string, input: CreateClientApiKeyInput): Promise<CreateClientApiKeyResult> => {
            const currentOperation = createOperations.current.get(contextUuid);
            if (currentOperation) return currentOperation;

            const operation = (async (): Promise<CreateClientApiKeyResult> => {
                try {
                    const result = await createClientApiKey({ contextUuid, input });
                    await queryClient.invalidateQueries({
                        queryKey: clientApiKeysQueryKey(contextUuid)
                    });
                    return result;
                } finally {
                    createOperations.current.delete(contextUuid);
                    setCreatingContexts((current) => {
                        const next = new Set(current);
                        next.delete(contextUuid);
                        return next;
                    });
                }
            })();
            createOperations.current.set(contextUuid, operation);
            setCreatingContexts((current) => new Set(current).add(contextUuid));
            return operation;
        },
        [queryClient]
    );
    const rotateKey = useCallback(
        async (id: string, input: RotateClientApiKeyInput): Promise<RotateClientApiKeyOutcome> => {
            const contextUuid = issuerClientUuid;
            if (!contextUuid) return { status: "stale" };
            const operationId = `${contextUuid}:${id}`;
            const currentOperation = rotateOperations.current.get(operationId);
            if (currentOperation) return currentOperation;

            const operation = (async (): Promise<
                | { readonly status: "success"; readonly result: RotateClientApiKeyResult; readonly error?: undefined }
                | { readonly status: "stale"; readonly result?: undefined; readonly error?: undefined }
                | { readonly status: "ambiguous"; readonly result?: undefined; readonly error?: undefined }
                | { readonly status: "error"; readonly error: unknown; readonly result?: undefined }
            > => {
                try {
                    const result = await rotateClientApiKey({ contextUuid, id, input });
                    await queryClient.invalidateQueries({ queryKey: clientApiKeysQueryKey(contextUuid) });
                    return isCurrentClientApiContext(contextUuid, useOrgAuthStore.getState().selectedClientContextUuid)
                        ? ({ status: "success", result } as const)
                        : ({ status: "stale" } as const);
                } catch (error) {
                    if (!isCurrentClientApiContext(contextUuid, useOrgAuthStore.getState().selectedClientContextUuid)) {
                        return { status: "stale" } as const;
                    }
                    if (isAmbiguousIssuanceError(error)) {
                        await queryClient.refetchQueries({ queryKey: clientApiKeysQueryKey(contextUuid) });
                        return isCurrentClientApiContext(
                            contextUuid,
                            useOrgAuthStore.getState().selectedClientContextUuid
                        )
                            ? ({ status: "ambiguous" } as const)
                            : ({ status: "stale" } as const);
                    }
                    return { status: "error", error } as const;
                } finally {
                    rotateOperations.current.delete(operationId);
                    setRotatingKeys((current) => {
                        const next = new Set(current);
                        next.delete(operationId);
                        return next;
                    });
                }
            })();
            rotateOperations.current.set(operationId, operation);
            setRotatingKeys((current) => new Set(current).add(operationId));
            return operation;
        },
        [issuerClientUuid, queryClient]
    );
    const revokeMutation = useMutation<void, Error, { contextUuid: string; id: string }>({
        mutationFn: revokeClientApiKey,
        retry: false,
        onSuccess: async (_result, variables) => {
            await queryClient.invalidateQueries({
                queryKey: clientApiKeysQueryKey(variables.contextUuid)
            });
        }
    });

    return {
        issuerClientUuid,
        query,
        createKey,
        isCreating: issuerClientUuid !== null && creatingContexts.has(issuerClientUuid),
        rotateKey,
        rotatingKeyIds: new Set(
            [...rotatingKeys]
                .filter((operationId) => operationId.startsWith(`${issuerClientUuid}:`))
                .map((operationId) => operationId.slice(operationId.indexOf(":") + 1))
        ),
        revokeMutation
    };
}
